<script setup lang="ts">
import dayjs from 'dayjs'
import WikiBitPcTopNav from '~/components/header/WikiBitPcTopNav.vue'
import { getRegulatorList, type RegulatorListItem } from '~/api/disclosure'
import { getRegulatorCodeByEnShortName, getRegulatorDetail } from '~/api/regulator-list'
import { normalizeDisclosureSlug } from '~/utils/disclosure-route'

definePageMeta({
  name: 'regulatory-disclosure',
  layout: 'default',
  hideDefaultTopNav: true,
})

type DisclosureItem = {
  id: string
  tag: string
  tagColor: string
  content: string
  traderName: string
  traderIco: string
  createTime: string
  regulatorSlug: string
  exchangeSlug: string
  disclosureSlug: string
  traderCode: string
}

type RegulatorDisclosureCategory = 'all' | 1 | 2 | 3
type InitialCategoryData = Record<RegulatorDisclosureCategory, Awaited<ReturnType<typeof getRegulatorList>>>

const PAGE_INDEX = 1
const PAGE_SIZE = 20
const DISCLOSURE_CATEGORIES: RegulatorDisclosureCategory[] = ['all', 3, 2, 1]

const { locale, localePath, t } = useAppI18n()
const route = useRoute()
const regulatorSlug = computed(() => {
  const value = route.params.slug
  return String(Array.isArray(value) ? value[0] || '' : value || '').trim()
})
const { data: regulatorDetailData } = await useAsyncData(
  () => `regulator-disclosure-detail-${regulatorSlug.value}`,
  () => getRegulatorDetail(regulatorSlug.value),
  {
    default: () => null,
    watch: [regulatorSlug],
  },
)
const regulatorDisplayName = computed(
  () =>
    regulatorDetailData.value?.regulatorName ||
    regulatorDetailData.value?.regulatorShortName ||
    regulatorDetailData.value?.shortName ||
    regulatorSlug.value,
)
const {
  data: regulatorCodeData,
  pending: regulatorCodePending,
  error: regulatorCodeError,
} = await useAsyncData(
  () => `regulator-disclosure-code-${regulatorSlug.value}`,
  async () => {
    const regulatorCode = await getRegulatorCodeByEnShortName(regulatorSlug.value)

    if (!regulatorCode) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
      })
    }

    return regulatorCode
  },
  {
    watch: [regulatorSlug],
  },
)
const regulatorCode = computed(() => regulatorCodeData.value || '')
const activeCategory = ref<RegulatorDisclosureCategory>('all')
const regulatorItems = ref<RegulatorListItem[]>([])
const currentPageIndex = ref(PAGE_INDEX)
const hasMoreItems = ref(true)
const isLoadingMoreItems = ref(false)
const hasMoreRequestError = ref(false)
const listRequestVersion = ref(0)
const loadMoreTriggerRef = ref<HTMLElement | null>(null)
let loadMoreObserver: IntersectionObserver | null = null

/** 首屏并行请求所有分类，后续当前分类第一页直接复用这里的结果。 */
const {
  data: initialCategoryData,
  pending: initialCategoryPending,
  error: initialCategoryError,
} = await useAsyncData(
  () => `regulator-disclosure-categories-${regulatorSlug.value}-${locale.value}`,
  async () => {
    if (!regulatorCode.value) {
      return null
    }

    const entries = await Promise.all(
      DISCLOSURE_CATEGORIES.map(async (category) => {
        const data = await getRegulatorList({
          cate: category,
          regulatorCodes: regulatorCode.value,
          pageindex: PAGE_INDEX,
          pagesize: PAGE_SIZE,
        })

        return [category, data] as const
      }),
    )

    return Object.fromEntries(entries) as InitialCategoryData
  },
  {
    default: () => null as InitialCategoryData | null,
    watch: [regulatorSlug, regulatorCode, locale],
  },
)

const categories = computed<Array<{ label: string; value: RegulatorDisclosureCategory }>>(() =>
  [
    { label: t('83794'), value: 'all' },
    { label: t('84664'), value: 3 },
    { label: t('84665'), value: 2 },
    { label: t('84666'), value: 1 },
  ].filter((category) => (initialCategoryData.value?.[category.value]?.items ?? []).length > 0),
)
const hasInitialDisclosureItems = computed(() =>
  DISCLOSURE_CATEGORIES.some((category) => (initialCategoryData.value?.[category]?.items ?? []).length > 0),
)
const regulatorListData = computed(() => initialCategoryData.value?.[activeCategory.value] ?? null)
const pending = computed(() => regulatorCodePending.value || initialCategoryPending.value)
const error = computed(() => initialCategoryError.value)
const hasDisclosureRequestError = computed(
  () => Boolean(regulatorCodeError.value) || Boolean(error.value) || hasMoreRequestError.value,
)

const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

/** RTL 页面使用日-月-年格式，LTR 页面保持年-月-日格式。 */
const formatDisclosureDate = (value: string) => {
  const isRtl = import.meta.client && document.documentElement.dir === 'rtl'
  const format = isRtl ? 'DD-MM-YYYY' : 'YYYY-MM-DD'

  return dayjs(value).format(format)
}

/** 首屏或筛选条件变化后重置分页列表状态。 */
watch(
  [regulatorListData, pending, regulatorSlug, locale, activeCategory],
  ([data]) => {
    regulatorItems.value = data?.items || []
    currentPageIndex.value = PAGE_INDEX
    hasMoreItems.value = regulatorItems.value.length === PAGE_SIZE
  },
  { immediate: true },
)

/** 筛选条件变化时先清空旧数据，避免新请求期间继续展示上一批内容。 */
watch([regulatorSlug, locale], () => {
  listRequestVersion.value += 1
  regulatorItems.value = []
  currentPageIndex.value = PAGE_INDEX
  hasMoreItems.value = true
  hasMoreRequestError.value = false
})

/** 分类变化时重置当前列表，但保留首屏 all 分类是否有数据的判断。 */
watch(activeCategory, () => {
  listRequestVersion.value += 1
  currentPageIndex.value = PAGE_INDEX
  hasMoreItems.value = true
  hasMoreRequestError.value = false
})

/** 获取下一页披露数据并追加到当前列表。 */
const loadMoreItems = async () => {
  if (pending.value || isLoadingMoreItems.value || !hasMoreItems.value) {
    return
  }

  isLoadingMoreItems.value = true
  const requestVersion = listRequestVersion.value
  const nextPageIndex = currentPageIndex.value + 1

  try {
    const data = await getRegulatorList({
      cate: activeCategory.value,
      regulatorCodes: regulatorCode.value,
      pageindex: nextPageIndex,
      pagesize: PAGE_SIZE,
    })

    if (requestVersion !== listRequestVersion.value) {
      return
    }

    regulatorItems.value.push(...data.items)
    currentPageIndex.value = nextPageIndex
    hasMoreItems.value = data.items.length === PAGE_SIZE
  } catch {
    hasMoreRequestError.value = true
  } finally {
    isLoadingMoreItems.value = false
  }
}

/** 监听列表底部触发元素，进入视口后自动加载下一批数据。 */
const observeLoadMoreTrigger = () => {
  loadMoreObserver?.disconnect()
  loadMoreObserver = null

  if (!loadMoreTriggerRef.value || typeof IntersectionObserver === 'undefined') {
    return
  }

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        void loadMoreItems()
      }
    },
    { rootMargin: '240px 0px' },
  )
  loadMoreObserver.observe(loadMoreTriggerRef.value)
}

onMounted(observeLoadMoreTrigger)
watch(loadMoreTriggerRef, observeLoadMoreTrigger)
onBeforeUnmount(() => {
  loadMoreObserver?.disconnect()
})

const disclosureItems = computed<DisclosureItem[]>(() =>
  regulatorItems.value.map((item) => {
    const tag = (item.tag || item.Tag) as Record<string, unknown> | string | undefined
    const trader = Array.isArray(item.traders) ? (item.traders[0] as Record<string, unknown> | undefined) : undefined
    const disclosureId = normalizeText(item.id || item.code || item.regId || item.RegId)
    const traderCode = normalizeText(trader?.code || trader?.Code || trader?.traderCode || trader?.TraderCode)
    const disclosureAlias = normalizeText(item.alias)
    const exchangeAlias = normalizeText(trader?.alias)

    return {
      id: disclosureId,
      tag: normalizeText(typeof tag === 'object' ? tag?.tagName : ''),
      tagColor: normalizeText(typeof tag === 'object' ? tag?.color : ''),
      content: normalizeText(item.content),
      traderName: normalizeText(trader?.localShortName || trader?.LocalShortName),
      traderIco: normalizeText(trader?.ico),
      createTime: normalizeText(item.createTime),
      regulatorSlug: regulatorSlug.value,
      exchangeSlug: normalizeDisclosureSlug(exchangeAlias, 'exchange'),
      disclosureSlug: disclosureAlias,
      traderCode,
    }
  }),
)

/** 生成监管披露详情页本地化链接，并将交易所 code 作为详情接口兜底参数传递。 */
const getDisclosurePath = (item: DisclosureItem) =>
  localePath({
    name: 'disclosures-detail',
    params: {
      regulatorSlug: regulatorSlug.value,
      exchangeSlug: item.exchangeSlug,
      slug: item.disclosureSlug,
    },
  })

const breadcrumbItems = computed(() => [
  { label: t('82520'), route: '/' },
  { label: t('82879'), route: localePath('/regulators') },
  { label: regulatorDisplayName.value, route: localePath(`/regulators/${regulatorSlug.value}`) },
  { label: t('82718') },
])

useHead({
  title: () => `${t('84874', [regulatorDisplayName.value])} | WikiBit`,
  meta: [
    {
      name: 'description',
      content: () => t('84883', [regulatorDisplayName.value]),
    },
  ],
})
</script>

<template>
  <CommonNotFound v-if="hasDisclosureRequestError" />
  <WikiBitPcTopNav :always-expand-search="true" :force-solid-background="true" />
  <main v-if="!hasDisclosureRequestError" class="min-h-screen bg-[#ffffff] pt-[70px] text-[#292a2e]">
    <div class="mx-auto w-[min(1200px,calc(100vw-48px))] pt-[24px] pb-[80px]">
      <CommonBreadcrumb :items="breadcrumbItems" class="regulatory-disclosure-breadcrumb" />

      <nav
        v-if="hasInitialDisclosureItems"
        class="mt-[24px] flex h-[60px] items-start gap-[24px] overflow-x-auto border-b border-[rgba(5,21,36,0.06)] pt-[16px]"
        :aria-label="t('82718')"
      >
        <button
          v-for="category in categories"
          :key="category.value"
          type="button"
          class="relative flex h-[44px] shrink-0 cursor-pointer flex-col items-center justify-between border-[0] bg-transparent p-[0] text-[20px] leading-[28px] transition-colors hover:text-[#292a2e]"
          :class="activeCategory === category.value ? 'font-[600] text-[#292a2e]' : 'font-[400] text-[#8c8f97]'"
          @click="activeCategory = category.value"
        >
          <span>{{ category.label }}</span>
          <span v-if="activeCategory === category.value" class="h-[4px] w-[28px] rounded-[999px] bg-[#292a2e]"></span>
        </button>
      </nav>

      <div
        v-if="pending"
        class="mt-[24px] flex h-[170px] items-center justify-center text-[14px] leading-[22px] text-[#8a9099]"
        role="status"
        aria-live="polite"
        aria-label="Loading disclosure list"
        aria-busy="true"
      >
        Loading...
      </div>
      <CommonEmptyState v-else-if="disclosureItems.length === 0" />
      <section v-else class="mt-[24px] grid grid-cols-2 gap-[16px]" :aria-label="t('82718')">
        <NuxtLink
          v-for="item in disclosureItems"
          :key="item.id"
          :to="getDisclosurePath(item)"
          target="_blank"
          class="flex h-[170px] cursor-pointer flex-col justify-between rounded-[4px] bg-[#f8f8f8] p-[20px] text-inherit no-underline transition-opacity hover:opacity-90"
        >
          <div class="flex min-h-0 flex-col gap-[8px]">
            <p class="m-[0] text-[16px] leading-[24px] font-[600]" :style="{ color: item.tagColor }">
              {{ item.tag }}
            </p>
            <h2 class="regulatory-disclosure-title m-[0] text-[18px] leading-[26px] font-[600] text-[#292a2e]">
              {{ item.content }}
            </h2>
          </div>

          <div class="flex items-center gap-[8px]">
            <span class="inline-flex items-center gap-[4px] rounded-[4px] bg-[#ffffff] p-[8px]">
              <CommonImage
                :src="item.traderIco"
                :alt="item.traderName"
                class="size-[16px] rounded-[2px]"
                width="16"
                height="16"
                object-fit="contain"
                loading="eager"
              />
              <span class="max-w-[220px] truncate text-[12px] leading-[18px] font-[400] text-[#505258]">
                {{ item.traderName }}
              </span>
            </span>
            <time class="text-[12px] leading-[18px] font-[400] whitespace-nowrap text-[#8c8f97]">
              {{ formatDisclosureDate(item.createTime) }}
            </time>
          </div>
        </NuxtLink>
      </section>
      <div
        v-if="!pending && disclosureItems.length > 0 && hasMoreItems"
        ref="loadMoreTriggerRef"
        class="flex h-[56px] items-center justify-center text-[14px] leading-[22px] text-[#8a9099]"
        role="status"
        aria-live="polite"
        aria-label="Loading more disclosure list"
        :aria-busy="isLoadingMoreItems"
      >
        {{ isLoadingMoreItems ? 'Loading...' : '' }}
      </div>
    </div>
  </main>
</template>

<style scoped lang="less">
.regulatory-disclosure-breadcrumb {
  padding: 0 !important;
}

.regulatory-disclosure-breadcrumb:deep(nav) {
  flex-wrap: wrap;
  padding: 0 !important;
  row-gap: 6px;
}

.regulatory-disclosure-title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow-wrap: break-word;
  word-break: break-word;
}
</style>
